/**
 * MaintenanceDueSection.js
 *
 * Phase 3: Maintenance Due section for Technician Dashboard
 * Displays devices with upcoming or overdue maintenance/calibration
 * Features: Quick stats cards + compact data table with inline actions
 *
 * Props:
 * - devices: Array of device objects with maintenance dates
 * - token: JWT authentication token
 * - onScheduleNow: Callback when "Log Work" button is clicked
 * - onViewDevice: Callback when "View" button is clicked
 */

import { useMemo, useState } from 'react';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import { getMaintenanceTypeLabel as getTypeLabel, getMaintenanceTypeIcon as getTypeIcon } from '../../devices/utils/maintenanceUtils';

const MaintenanceDueSection = ({ devices = [], token, onScheduleNow, onViewDevice }) => {
  const [sortConfig, setSortConfig] = useState({
    column: 'dueDate',
    direction: 'asc' // 'asc' or 'desc'
  });

  // Helper function to calculate days until due
  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const days = Math.floor((due - today) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };


  // Helper function to get status badge
  const getMaintenanceStatus = (daysUntilDue) => {
    if (daysUntilDue === null) return { label: 'N/A', class: 'status-unknown' };
    if (daysUntilDue < 0) return { label: 'Overdue', class: 'status-overdue' };
    if (daysUntilDue === 0) return { label: 'Due Today', class: 'status-due-today' };
    if (daysUntilDue <= 7) return { label: 'Due Soon', class: 'status-due-soon' };
    if (daysUntilDue <= 30) return { label: 'Due This Month', class: 'status-due-month' };
    return { label: 'Upcoming', class: 'status-upcoming' };
  };

  // Helper function to handle column sort
  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Helper function to apply sort to maintenance items
  const applySorting = (items) => {
    const { column, direction } = sortConfig;
    const sorted = [...items];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (column) {
        case 'type':
          aValue = a.maintenanceType.toLowerCase();
          bValue = b.maintenanceType.toLowerCase();
          break;
        case 'device':
          aValue = a.deviceName.toLowerCase();
          bValue = b.deviceName.toLowerCase();
          break;
        case 'department':
          aValue = a.department.toLowerCase();
          bValue = b.department.toLowerCase();
          break;
        case 'dueDate':
          aValue = new Date(a.dueDate).getTime();
          bValue = new Date(b.dueDate).getTime();
          break;
        case 'daysUntilDue':
        default:
          aValue = a.daysUntilDue !== null ? a.daysUntilDue : Infinity;
          bValue = b.daysUntilDue !== null ? b.daysUntilDue : Infinity;
          break;
      }

      // Handle null/undefined values
      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      // Compare values
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };


  // Process maintenance data
  const maintenanceItems = useMemo(() => {
    const items = [];

    devices.forEach((device) => {
      // PPM (Preventive Maintenance)
      if (device.ppm_next_due_date) {
        const daysUntilDue = getDaysUntilDue(device.ppm_next_due_date);
        // Only include if due within next 30 days (or already overdue)
        if (daysUntilDue !== null && daysUntilDue <= 30) {
          items.push({
            id: `${device.id}-ppm`,
            deviceId: device.id,
            deviceIdentifier: device.device_identifier || 'N/A',
            deviceName: device.device_name || device.name || 'N/A',
            maintenanceType: 'PPM',
            dueDate: device.ppm_next_due_date,
            lastMaintenanceDate: device.ppm_last_done_date || null,
            daysUntilDue,
            warrantyExpiry: device.warranty_expiry || null,
            department: device.department || 'N/A',
            departmentIcon: device.department_icon || '🏢',
            otherLastMaintenanceDate: device.calibration_last_done_date || null,
            otherMaintenanceType: 'Calibration'
          });
        }
      }

      // Calibration
      if (device.calibration_next_due_date) {
        const daysUntilDue = getDaysUntilDue(device.calibration_next_due_date);
        // Only include if due within next 30 days (or already overdue)
        if (daysUntilDue !== null && daysUntilDue <= 30) {
          items.push({
            id: `${device.id}-calibration`,
            deviceId: device.id,
            deviceIdentifier: device.device_identifier || 'N/A',
            deviceName: device.device_name || device.name || 'N/A',
            maintenanceType: 'Calibration',
            dueDate: device.calibration_next_due_date,
            lastMaintenanceDate: device.calibration_last_done_date || null,
            daysUntilDue,
            warrantyExpiry: device.warranty_expiry || null,
            department: device.department || 'N/A',
            departmentIcon: device.department_icon || '🏢',
            otherLastMaintenanceDate: device.ppm_last_done_date || null,
            otherMaintenanceType: 'PPM'
          });
        }
      }

      // Compliance
      if (device.compliance_next_due_date) {
        const daysUntilDue = getDaysUntilDue(device.compliance_next_due_date);
        // Only include if due within next 30 days (or already overdue)
        if (daysUntilDue !== null && daysUntilDue <= 30) {
          items.push({
            id: `${device.id}-compliance`,
            deviceId: device.id,
            deviceIdentifier: device.device_identifier || 'N/A',
            deviceName: device.device_name || device.name || 'N/A',
            maintenanceType: 'Compliance',
            complianceType: device.compliance_type || 'Regulatory',
            dueDate: device.compliance_next_due_date,
            lastMaintenanceDate: device.compliance_last_done_date || null,
            daysUntilDue,
            warrantyExpiry: device.warranty_expiry || null,
            department: device.department || 'N/A',
            departmentIcon: device.department_icon || '🏢',
            otherLastMaintenanceDate: null,
            otherMaintenanceType: null
          });
        }
      }
    });

    // Apply sorting based on current sort configuration
    return applySorting(items);
  }, [devices, sortConfig]);

  // Calculate stats
  const stats = useMemo(() => {
    const overdue = maintenanceItems.filter(item => item.daysUntilDue !== null && item.daysUntilDue < 0).length;
    const thisWeek = maintenanceItems.filter(item => item.daysUntilDue !== null && item.daysUntilDue >= 0 && item.daysUntilDue <= 7).length;
    const thisMonth = maintenanceItems.filter(item => item.daysUntilDue !== null && item.daysUntilDue > 7 && item.daysUntilDue <= 30).length;
    const next30Days = maintenanceItems.filter(item => item.daysUntilDue !== null && item.daysUntilDue > 30).length;

    return { overdue, thisWeek, thisMonth, next30Days };
  }, [maintenanceItems]);


  if (maintenanceItems.length === 0) {
    return (
      <div className="maintenance-due-section">
        <h3 className="dashboard-section-title">🔧 Maintenance Due <span className="subtitle-text">(Next 30 Days)</span></h3>
        <div className="no-maintenance-items">
          <p>✅ No maintenance scheduled</p>
          <p className="subtext">All devices are up to date for the next 30 days</p>
        </div>
      </div>
    );
  }

  return (
    <div className="maintenance-due-section">
      <h3 className="dashboard-section-title">🔧 Maintenance Due <span className="subtitle-text">(Next 30 Days)</span></h3>

      {/* Quick Stats Cards */}
      <div className="maintenance-stats-grid">
        <div className="maintenance-stat-card overdue">
          <div className="stat-icon">🔴</div>
          <div className="stat-value">{stats.overdue}</div>
          <div className="stat-label">Overdue</div>
        </div>

        <div className="maintenance-stat-card due-soon">
          <div className="stat-icon">⚠️</div>
          <div className="stat-value">{stats.thisWeek}</div>
          <div className="stat-label">Due This Week</div>
        </div>

        <div className="maintenance-stat-card due-month">
          <div className="stat-icon">📅</div>
          <div className="stat-value">{stats.thisMonth}</div>
          <div className="stat-label">Due This Month</div>
        </div>

        <div className="maintenance-stat-card upcoming">
          <div className="stat-icon">✅</div>
          <div className="stat-value">{stats.next30Days}</div>
          <div className="stat-label">Due Next 30 Days</div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="maintenance-table-container">
        <table className="maintenance-table">
          <thead>
            <tr>
              <th
                className={`col-type sortable ${sortConfig.column === 'type' ? `sort-active sort-${sortConfig.direction}` : ''}`}
                onClick={() => handleSort('type')}
              >
                Type
                {sortConfig.column === 'type' && (
                  <span className="sort-indicator">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th
                className={`col-device sortable ${sortConfig.column === 'device' ? `sort-active sort-${sortConfig.direction}` : ''}`}
                onClick={() => handleSort('device')}
              >
                Device
                {sortConfig.column === 'device' && (
                  <span className="sort-indicator">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th
                className={`col-department sortable ${sortConfig.column === 'department' ? `sort-active sort-${sortConfig.direction}` : ''}`}
                onClick={() => handleSort('department')}
              >
                Department
                {sortConfig.column === 'department' && (
                  <span className="sort-indicator">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th
                className={`col-due-date sortable ${sortConfig.column === 'dueDate' ? `sort-active sort-${sortConfig.direction}` : ''}`}
                onClick={() => handleSort('dueDate')}
              >
                Due Date
                {sortConfig.column === 'dueDate' && (
                  <span className="sort-indicator">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th
                className={`col-status sortable ${sortConfig.column === 'daysUntilDue' ? `sort-active sort-${sortConfig.direction}` : ''}`}
                onClick={() => handleSort('daysUntilDue')}
              >
                Status
                {sortConfig.column === 'daysUntilDue' && (
                  <span className="sort-indicator">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {maintenanceItems.map((item) => {
              const status = getMaintenanceStatus(item.daysUntilDue);

              return (
                <tr key={item.id} className={`maintenance-row ${status.class}`}>
                  <td className="col-type">
                    <span className="maintenance-type-badge" data-type={item.maintenanceType.toLowerCase()}>
                      <span className="type-icon">{getTypeIcon(item.maintenanceType)}</span>
                      <span className="type-label">{getTypeLabel(item.maintenanceType)}</span>
                    </span>
                  </td>
                  <td className="col-device">
                    <div className="device-stacked">
                      <span className="device-name">{item.deviceName}</span>
                      <DeviceIDBadge deviceId={item.deviceIdentifier} size="small" />
                    </div>
                  </td>
                  <td className="col-department">
                    <span className="department-cell">
                      <span className="department-icon-small" aria-hidden="true">
                        {item.departmentIcon}
                      </span>
                      <span className="department-text">{item.department}</span>
                    </span>
                  </td>
                  <td className="col-due-date">
                    <div className="due-date-stacked">
                      <span className="due-date-value">{formatDate(item.dueDate)}</span>
                      <span className={`days-left-bold ${item.daysUntilDue !== null && item.daysUntilDue < 7 ? 'urgent' : ''}`}>
                        {item.daysUntilDue !== null ? (
                          <>
                            {Math.abs(item.daysUntilDue)} day{Math.abs(item.daysUntilDue) !== 1 ? 's' : ''}
                            {item.daysUntilDue < 0 ? ' overdue' : ' left'}
                          </>
                        ) : (
                          'N/A'
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="col-status">
                    <span className={`status-badge ${status.class}`}>{status.label}</span>
                  </td>
                  <td className="col-actions">
                    <div className="action-buttons-inline">
                      <button
                        className="action-btn-small log-work"
                        title="Log completed maintenance work"
                        onClick={() => onScheduleNow && onScheduleNow(item.deviceId, item.maintenanceType)}
                      >
                        Log Work
                      </button>
                      <button
                        className="action-btn-small details"
                        title="View device details"
                        onClick={() => onViewDevice && onViewDevice(item.deviceId)}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MaintenanceDueSection;
